import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// @mui
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import Message from '@mui/icons-material/Message';
import Drafts from '@mui/icons-material/Drafts';

// core
import { withCatch } from '_core/hocs/withCatch';
import { withLang } from '_core/hocs/withLang';

import { useGlobal } from '_core/hooks/useGlobal';

import { withVisitorInfo } from '_core/utils/visitorInfo';

import { Menu } from '_core/components/Menu';
import { Loading } from '_core/components/Loading';
import { MessagesSplashView } from '_core/views/MessagesSplash';
import { ModalOuter } from '_core/components/ModalOuter';

import {
  isMine,
  loadMessage,
  loadTopic
} from '_core/views/MessagesSplash/util.js';

// style
import { useStyles } from './headerControlsStyle';

const RawMessageNotifier = (props) => {
  const { visitorInfo, getLangString: l } = props;

  const classes = useStyles();

  const {
    request: { queue },
    config: { API_METHOD, API_NOTICE }
  } = useGlobal();

  const [state, setState] = useState({ topicId: null, isOpen: false });

  React.useEffect(() => {
    if (window.location.pathname === '/login') {
      return;
    }
    
    queue(API_METHOD.VISITOR_MESSAGE_LIST_UNREAD, {}).then((result) => {
      if (!result || !result.list) {
        return;
      }
      
      const data = result.list.map((x) => {
        return {
          ...loadMessage(x.message),
          topic: loadTopic(x.topic)
        };
      });

      visitorInfo.unreadMessagesInit(data);
    }).catch(() => {});
  }, [API_METHOD.VISITOR_MESSAGE_LIST_UNREAD]);

  React.useEffect(() => {
    const notifyListener = (event) => {
      if (event.detail.name === API_NOTICE.VISITOR_MESSAGE_CREATE) {
        const messageId = event.detail.data.id;

        if (messageId) {
          queue(
            API_METHOD.VISITOR_MESSAGE_FIND,
            { message_id: messageId },
            RawMessageNotifier.displayName
          ).then((result) => {
            if (!result || !result.message) {
              return;
            }
            const message = loadMessage(result.message);
            const topic = loadTopic(result.topic);
            if (message.isDashboardShow || !isMine(visitorInfo, message)) {
              visitorInfo.unreadMessageReceived({ ...message, topic: topic });
            }
          }).catch(() => {});
        }
      } else if (event.detail.name === API_NOTICE.VISITOR_MESSAGE_HAD_READ) {
        visitorInfo.messagesHadRead(event.detail.data.message_ids);
      }
    };

    window.addEventListener('notify', notifyListener);

    return () => window.removeEventListener('notify', notifyListener);
  }, []);

  const unreadCount = visitorInfo.unreadMessages?.length ?? 0;

  const renderButton = (btnProps) => (
    <IconButton {...btnProps}>
      <Badge
        classes={{ badge: classes.userNotifierBadge }}
        color="primary"
        badgeContent={unreadCount}
        max={99}
      >
        <Message />
      </Badge>
    </IconButton>
  );

  const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });

  const markMessagesRead = (messageIds) => {
    if (!messageIds.length) {
      return;
    }

    visitorInfo.messagesHadRead(messageIds);

    queue(
      API_METHOD.VISITOR_MESSAGE_HAD_READ,
      { message_ids: messageIds },
      RawMessageNotifier.displayName
    ).catch(() => {});
  };

  const renderItem = ({ onPopoverClose }, item) => {
    const onClick = () => {
      markMessagesRead([item.id]);
      onPopoverClose();
      setState({ topicId: item.topic.id, isOpen: true });
    };

    const timestamp = new Date(item.timestamp);

    return (
      <ListItem
        button
        key={item.id}
        component={Link}
        to="mmm"
        onClick={onClick}
      >
        <ListItemIcon>
          <Drafts />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography className={classes.userNotifierListItemPrimary} noWrap>
              {String(item.content || '')
                .trim()
                .replace(/[\n\r][\s\S]*/, '…')}
            </Typography>
          }
          secondary={dateTimeFormat.format(timestamp)}
        />
      </ListItem>
    );
  };

  const renderContent = (props) => (
    <List
      component="nav"
      subheader={
        <ListSubheader component="div">
          {l('r._.header.messenger')}

          {!visitorInfo.unreadMessages ? <Loading /> : null}
        </ListSubheader>
      }
    >
      {visitorInfo.unreadMessages != null ? (
        visitorInfo.unreadMessages.length ? (
          visitorInfo.unreadMessages.map((item) => renderItem(props, item))
        ) : (
          <ListItem
            button
            onClick={() => {
              const { onPopoverClose } = props;

              onPopoverClose();
              setState({ topicId: null, isOpen: true });
            }}
          >
            <ListItemText secondary={l('r._.header.messenger.button.title')} />
          </ListItem>
        )
      ) : null}
    </List>
  );

  const onClose = () => {
    setState((prev) => {
      return { ...prev, ...{ isOpen: false } };
    });
  };

  return (
    <>
      <Menu
        ButtonComponent={renderButton}
        renderContent={renderContent}
        tooltip={l('r._.header.messenger')}
        paperProps={{ className: classes.userNotifierPaper }}
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }}
      />
      <ModalOuter open={state.isOpen} closeIcon={true} onClose={onClose}>
        <MessagesSplashView topicId={state.topicId} />
      </ModalOuter>
    </>
  );
};

RawMessageNotifier.defaultProps = {};

RawMessageNotifier.propTypes = {
  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const MessageNotifier = withCatch(
  withVisitorInfo(withLang(RawMessageNotifier))
);
